import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { submitLoanInquiry } from '../services/api';
import { Loader2, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const InquiryForm = ({ isOpen, onClose, loanType }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    loanType: loanType || '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    propertyAddress: '',
    purchasePrice: '',
    rehabBudget: '',
    arv: '',
    exitStrategy: '',
    message: ''
  });
  const [smsOptIn, setSmsOptIn] = useState(false);

  // Update loanType when prop changes
  useEffect(() => {
    if (loanType) {
      setFormData(prev => ({ ...prev, loanType }));
    }
  }, [loanType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await submitLoanInquiry({
        ...formData,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
        rehabBudget: formData.rehabBudget ? parseFloat(formData.rehabBudget) : null,
        arv: formData.arv ? parseFloat(formData.arv) : null
      });

      setSuccess(true);
      toast({
        title: "Success!",
        description: response.message,
      });

      // Reset form after 3 seconds and close
      setTimeout(() => {
        setFormData({
          loanType: loanType || '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          propertyAddress: '',
          purchasePrice: '',
          rehabBudget: '',
          arv: '',
          exitStrategy: '',
          message: ''
        });
        setSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit inquiry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Get Pre-Qualified Today
          </DialogTitle>
          <DialogDescription>
            Fill out the form below and we'll contact you within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-16 w-16 text-green-600 mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
            <p className="text-gray-600 text-center">
              Your inquiry has been submitted successfully.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="loanType">Loan Type *</Label>
              <select
                id="loanType"
                name="loanType"
                data-testid="loan-type-select"
                value={formData.loanType}
                onChange={handleChange}
                required
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select a loan type</option>
                <option value="Cash Out Refinance">Cash Out Refinance</option>
                <option value="Hard Money">Hard Money</option>
                <option value="Fix and Flip">Fix and Flip</option>
                <option value="New Construction">New Construction</option>
                <option value="Commercial or Multi-Family">Commercial or Multi-Family</option>
                <option value="Short Term Rental">Short Term Rental</option>
              </select>
            </div>

            <div>
              <Label htmlFor="propertyAddress">Property Address</Label>
              <Input
                id="propertyAddress"
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="purchasePrice">Purchase Price</Label>
                <Input
                  id="purchasePrice"
                  name="purchasePrice"
                  type="number"
                  placeholder="$"
                  value={formData.purchasePrice}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="rehabBudget">Rehab Budget</Label>
                <Input
                  id="rehabBudget"
                  name="rehabBudget"
                  type="number"
                  placeholder="$"
                  value={formData.rehabBudget}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="arv">ARV</Label>
                <Input
                  id="arv"
                  name="arv"
                  type="number"
                  placeholder="$"
                  value={formData.arv}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="exitStrategy">Exit Strategy</Label>
              <Input
                id="exitStrategy"
                name="exitStrategy"
                value={formData.exitStrategy}
                onChange={handleChange}
                className="mt-1"
                placeholder="e.g., Sell, Refinance, Rent"
              />
            </div>

            <div>
              <Label htmlFor="message">Additional Information</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="sms-optin-inquiry"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                required
                data-testid="inquiry-sms-optin"
              />
              <label htmlFor="sms-optin-inquiry" className="text-xs text-gray-500 leading-relaxed">
                I agree to be contacted by Close Now Lending Corp via call, email, and text for financial services. To opt out of text messages, you can reply 'stop' at any time or reply 'help' for assistance. You can also select the unsubscribe link in the emails. Message and data rates may apply. Message frequency may vary. <a href="/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a> | <a href="/terms" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Inquiry'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InquiryForm;